import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Modal } from '../../components/ui/UI';
import { format, formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { Users, UserCheck, UserX, Clock, UserPlus, Search, X, RefreshCw, AlertCircle, Inbox, Trash2, Building2, Check } from 'lucide-react';
import './AdminLoginsPage.css';

const isActive = (a) => a.isActive !== false;
const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';
const PLANTS = ['SNACKS', 'DAIRIES', 'SWAN'];
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

function PlantPicker({ selected, onChange, disabled = false }) {
  return (
    <fieldset className="plant-picker" disabled={disabled}>
      <legend>
        <span>Plant access</span>
        <span className="plant-picker-count">{selected.length} selected</span>
      </legend>
      <div className="plant-choice-grid">
        {PLANTS.map((plant) => {
          const checked = selected.includes(plant);
          return (
            <label key={plant} className={`plant-choice ${checked ? 'plant-choice--selected' : ''}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange((current) => event.target.checked
                  ? [...current, plant]
                  : current.filter((item) => item !== plant))}
              />
              <span className="plant-choice-icon">{checked ? <Check size={15} /> : <Building2 size={15} />}</span>
              <span className="plant-choice-label">
                <strong>{plant}</strong>
                <small>Plant records and reports</small>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export default function AdminLoginsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [invitePlants, setInvitePlants] = useState([]);
  const [creating, setCreating] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [plantAdmin, setPlantAdmin] = useState(null);
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [savingPlants, setSavingPlants] = useState(false);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/admins');
      setAdmins(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load administrators.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const stats = useMemo(() => ({
    total: admins.length,
    active: admins.filter(isActive).length,
    inactive: admins.filter((a) => !isActive(a)).length,
    never: admins.filter((a) => !a.lastLogin).length,
  }), [admins]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter((a) => {
      if (tab === 'active' && !isActive(a)) return false;
      if (tab === 'inactive' && isActive(a)) return false;
      return !q || `${a.fullName} ${a.email}`.toLowerCase().includes(q);
    });
  }, [admins, tab, search]);

  const createAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post('/auth/admins', {
        fullName: fullName.trim(),
        email: email.trim(),
        plants: invitePlants
      });
      setFullName('');
      setEmail('');
      setInvitePlants([]);
      setInviteOpen(false);
      toast.success(data.message || 'Invitation sent');
      await fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create admin. Check the details and try again.');
    } finally {
      setCreating(false);
    }
  };

  const closeInvite = () => {
    if (creating) return;
    setInviteOpen(false);
    setFullName('');
    setEmail('');
    setInvitePlants([]);
  };

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { admin, action } = confirmAction;
    setBusyId(admin._id);
    try {
      if (action === 'delete') {
        await api.delete(`/auth/admins/${admin._id}`);
        setAdmins((current) => current.filter((item) => item._id !== admin._id));
        toast.success('Admin deleted');
      } else {
        const { data } = await api.patch(`/auth/admins/${admin._id}/status`, { isActive: action === 'reactivate' });
        setAdmins((current) => current.map((item) => (item._id === data._id ? data : item)));
        toast.success(data.isActive ? 'Admin reactivated' : 'Admin deactivated');
      }
      setConfirmAction(null);
    } catch (err) {
      toast.error(err.response?.data?.message || `Could not ${action} admin`);
    } finally {
      setBusyId(null);
    }
  };

  const openPlantAssignment = (admin) => {
    setPlantAdmin(admin);
    setSelectedPlants(admin.plants || []);
  };

  const savePlantAssignment = async () => {
    if (!plantAdmin) return;
    setSavingPlants(true);
    try {
      const { data } = await api.patch(`/auth/admins/${plantAdmin._id}/plants`, { plants: selectedPlants });
      setAdmins((current) => current.map((item) => (item._id === data._id ? data : item)));
      setPlantAdmin(null);
      toast.success('Plant access updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update plant access');
    } finally {
      setSavingPlants(false);
    }
  };

  const cards = [
    { icon: Users, label: 'Administrators', value: stats.total, tone: 'ink' },
    { icon: UserCheck, label: 'Active', value: stats.active, tone: 'safe' },
    { icon: UserX, label: 'Inactive', value: stats.inactive, tone: 'risk' },
    { icon: Clock, label: 'Never signed in', value: stats.never, tone: 'ink' },
  ];
  const cols = 7;

  return (
    <div className="adm">
      <header className="adm-head">
        <div>
          <h1>Administrators</h1>
          <p>Invite people to manage the register and control who has access.</p>
        </div>
        <div className="adm-head-actions">
          <button className="btn btn--solid" onClick={() => setInviteOpen(true)}>
            <UserPlus size={15} /> Invite administrator
          </button>
          <button className="btn btn--ghost" onClick={fetchAdmins} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </header>

      <section className="stats" aria-label="Administrator summary">
        {cards.map(({ icon: Icon, label, value, tone }) => (
          <div key={label} className={`stat stat--${tone}`}>
            <span className="stat-ic"><Icon size={18} /></span>
            <div>
              <div className="stat-val">{loading ? '–' : value}</div>
              <div className="stat-lbl">{label}</div>
            </div>
          </div>
        ))}
      </section>

      {error && (
        <div className="alert" role="alert">
          <AlertCircle size={17} />
          <span>{error}</span>
          <button className="link-btn" onClick={fetchAdmins}>Try again</button>
        </div>
      )}

      <section className="panel panel--flush">
        <div className="bar">
          <div className="tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t.key} role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'tab tab--on' : 'tab'} onClick={() => setTab(t.key)}>
                {t.label}
                <span>{t.key === 'all' ? stats.total : t.key === 'active' ? stats.active : stats.inactive}</span>
              </button>
            ))}
          </div>
          <div className="search">
            <Search size={15} />
            <input placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search administrators" />
            {search && <button onClick={() => setSearch('')} aria-label="Clear search"><X size={14} /></button>}
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th className="col-sn">S/N</th>
                <th>Administrator</th>
                <th>Email</th>
                <th>Plants</th>
                <th>Last sign-in</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, r) => (
                  <tr key={r}>{Array.from({ length: cols }).map((__, c) => <td key={c}><span className="skel" /></td>)}</tr>
                ))
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={cols}>
                    <div className="empty">
                      <Inbox size={34} />
                      <h3>{admins.length === 0 ? 'No administrators yet' : 'No administrators match'}</h3>
                      <p>{admins.length === 0 ? 'Use the invite form above to add the first one.' : 'Try a different search or status.'}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                visible.map((a, i) => {
                  const active = isActive(a);
                  return (
                    <tr key={a._id}>
                      <td className="col-sn">{i + 1}</td>
                      <td>
                        <div className="person">
                          <span className={`avatar ${active ? '' : 'avatar--off'}`}>{initials(a.fullName)}</span>
                          <b>{a.fullName}</b>
                        </div>
                      </td>
                      <td className="col-muted">{a.email}</td>
                      <td>
                        <div className="plant-assignment-cell">
                          <span>{a.role === 'superadmin' ? 'All plants' : (a.plants || []).join(', ') || 'None assigned'}</span>
                          {a.role !== 'superadmin' && (
                            <button className="link-btn" onClick={() => openPlantAssignment(a)}>Manage</button>
                          )}
                        </div>
                      </td>
                      <td>
                        {a.lastLogin ? (
                          <div className="login" title={format(new Date(a.lastLogin), 'dd MMM yyyy, HH:mm')}>
                            <span>{format(new Date(a.lastLogin), 'dd MMM yyyy, HH:mm')}</span>
                            <small>{formatDistanceToNow(new Date(a.lastLogin), { addSuffix: true })}</small>
                          </div>
                        ) : (
                          <span className="never">Never signed in</span>
                        )}
                      </td>
                      <td><span className={`badge ${active ? 'badge--safe' : 'badge--risk'}`}>{active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div className="row-actions">
                          <button
                            className={active ? 'btn-row btn-row--danger' : 'btn-row btn-row--ok'}
                            disabled={busyId === a._id}
                            onClick={() => setConfirmAction({ admin: a, action: active ? 'deactivate' : 'reactivate' })}
                          >
                            {busyId === a._id ? 'Saving…' : active ? 'Deactivate' : 'Reactivate'}
                          </button>
                          <button
                            className="btn-row btn-row--delete"
                            disabled={busyId === a._id}
                            onClick={() => setConfirmAction({ admin: a, action: 'delete' })}
                            title={`Delete ${a.fullName}`}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title="Invite an administrator"
        className="admin-dialog admin-dialog--invite"
      >
        <form className="invite invite--modal" onSubmit={createAdmin}>
          <div className="admin-dialog-intro">
            <span className="admin-dialog-icon"><UserPlus size={18} /></span>
            <p>Add their details and choose one or more plants for their access.</p>
          </div>
          <label className="admin-form-field">
            <span>Full name</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required maxLength={120} placeholder="e.g. Amaka Okafor" autoComplete="off" />
          </label>
          <label className="admin-form-field">
            <span>Work email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="name@company.com" autoComplete="off" />
          </label>
          <PlantPicker selected={invitePlants} onChange={setInvitePlants} disabled={creating} />
          <div className="admin-dialog-actions">
            <button type="button" className="btn btn--ghost" onClick={closeInvite} disabled={creating}>Cancel</button>
            <button type="submit" className="btn btn--solid" disabled={creating || invitePlants.length === 0}>
              {creating ? 'Sending invite…' : 'Send invite'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!confirmAction}
        onClose={() => busyId || setConfirmAction(null)}
        title={confirmAction?.action === 'delete' ? 'Delete administrator?' : `${confirmAction?.action === 'reactivate' ? 'Reactivate' : 'Deactivate'} administrator?`}
        className="admin-dialog admin-dialog--confirm"
      >
        <div className={`admin-confirm admin-confirm--${confirmAction?.action || 'deactivate'}`}>
          <span className="admin-confirm-icon">
            {confirmAction?.action === 'delete' ? <Trash2 size={20} /> : confirmAction?.action === 'reactivate' ? <UserCheck size={20} /> : <UserX size={20} />}
          </span>
          <p>
            {confirmAction?.action === 'delete' ? (
              <><strong>{confirmAction.admin.fullName}</strong> will be permanently removed and lose all access. This cannot be undone.</>
            ) : confirmAction?.action === 'deactivate' ? (
              <><strong>{confirmAction.admin.fullName}</strong> will lose access immediately. You can reactivate the account later.</>
            ) : (
              <><strong>{confirmAction?.admin.fullName}</strong> will regain access to the admin system.</>
            )}
          </p>
          <div className="admin-dialog-actions">
            <button className="btn btn--ghost" onClick={() => setConfirmAction(null)} disabled={!!busyId}>Cancel</button>
            <button
              className={`btn ${confirmAction?.action === 'reactivate' ? 'btn--solid' : 'btn--danger'}`}
              onClick={runConfirmedAction}
              disabled={!!busyId}
            >
              {busyId ? 'Saving…' : confirmAction?.action === 'delete' ? 'Delete admin' : confirmAction?.action === 'reactivate' ? 'Reactivate' : 'Deactivate'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!plantAdmin}
        onClose={() => !savingPlants && setPlantAdmin(null)}
        title="Manage plant access"
        className="admin-dialog admin-dialog--plants"
      >
        <div className="plant-editor">
          <div className="admin-dialog-intro admin-dialog-intro--person">
            <span className="admin-dialog-icon"><Building2 size={18} /></span>
            <div><strong>{plantAdmin?.fullName}</strong><span>{plantAdmin?.email}</span></div>
          </div>
          <PlantPicker selected={selectedPlants} onChange={setSelectedPlants} disabled={savingPlants} />
          <div className="admin-dialog-actions">
            <button className="btn btn--ghost" onClick={() => setPlantAdmin(null)} disabled={savingPlants}>Cancel</button>
            <button className="btn btn--solid" onClick={savePlantAssignment} disabled={savingPlants}>
              {savingPlants ? 'Saving…' : 'Save plant access'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}